import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import UserConfirmDialog from "./UserConfirmDialog";
import {
    adminConfirmUserEmailChange,
    adminConfirmUserEmailVerification,
    adminRequestUserEmailChange,
    adminRequestUserEmailVerification,
    adminResetUserPassword,
} from "../../actions/user.actions";

const CODE_TTL_SECONDS = 300;

const UserSecurityBlock = ({
    userDetails = {},
    registerGuard,
}) => {
    const dispatch = useDispatch();
    const emailVerified = !!(userDetails.isActivated || userDetails.emailVerifiedAt);

    const [verifyStep, setVerifyStep] = useState("idle");
    const [verifyCode, setVerifyCode] = useState("");
    const [verifySeconds, setVerifySeconds] = useState(0);
    const [verifyBusy, setVerifyBusy] = useState(false);
    const [verifyError, setVerifyError] = useState("");
    const [verifyMessage, setVerifyMessage] = useState("");

    const [newEmail, setNewEmail] = useState("");
    const [pendingEmail, setPendingEmail] = useState("");
    const [emailStep, setEmailStep] = useState("request");
    const [emailCode, setEmailCode] = useState("");
    const [emailSeconds, setEmailSeconds] = useState(0);
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [emailMessage, setEmailMessage] = useState("");

    const [passwordConfirmOpen, setPasswordConfirmOpen] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [passwordMessage, setPasswordMessage] = useState("");

    useEffect(() => {
        setVerifyStep("idle");
        setVerifyCode("");
        setVerifySeconds(0);
        setVerifyError("");
        setVerifyMessage("");
        setNewEmail("");
        setPendingEmail("");
        setEmailStep("request");
        setEmailCode("");
        setEmailSeconds(0);
        setEmailError("");
        setEmailMessage("");
        setPasswordConfirmOpen(false);
        setPasswordError("");
        setPasswordMessage("");
    }, [userDetails?._id, userDetails?.email]);

    useEffect(() => {
        if (verifySeconds <= 0) return undefined;
        const timer = setInterval(() => setVerifySeconds((value) => Math.max(value - 1, 0)), 1000);
        return () => clearInterval(timer);
    }, [verifySeconds]);

    useEffect(() => {
        if (emailSeconds <= 0) return undefined;
        const timer = setInterval(() => setEmailSeconds((value) => Math.max(value - 1, 0)), 1000);
        return () => clearInterval(timer);
    }, [emailSeconds]);

    const securityDirty = useMemo(() => (
        verifyStep === "confirm" ||
        emailStep === "confirm" ||
        !!String(newEmail || "").trim()
    ), [emailStep, newEmail, verifyStep]);

    const resetDrafts = useCallback(() => {
        setVerifyStep("idle");
        setVerifyCode("");
        setVerifySeconds(0);
        setVerifyError("");
        setVerifyMessage("");
        setNewEmail("");
        setPendingEmail("");
        setEmailStep("request");
        setEmailCode("");
        setEmailSeconds(0);
        setEmailError("");
        setEmailMessage("");
        return true;
    }, []);

    useEffect(() => {
        registerGuard?.({
            isDirty: securityDirty,
            canSave: false,
            title: "Закрыть раздел безопасности?",
            text: "Есть незавершённая операция с email. Если выйти, введённые данные и текущий экран подтверждения будут сброшены.",
            details: securityDirty ? [{
                label: "Незавершённая операция",
                before: "Текущий раздел открыт",
                after: "Черновик будет сброшен",
            }] : [],
            onDiscard: resetDrafts,
        });

        return () => registerGuard?.(null);
    }, [registerGuard, resetDrafts, securityDirty]);

    const requestVerificationCode = async () => {
        if (verifyBusy || emailVerified) return;

        setVerifyBusy(true);
        setVerifyError("");
        setVerifyMessage("");

        const result = await dispatch(adminRequestUserEmailVerification(userDetails._id));

        setVerifyBusy(false);

        if (!result?.ok) {
            setVerifyError(result?.message || "Не удалось отправить код подтверждения");
            if (result?.secondsLeft) setVerifySeconds(result.secondsLeft);
            return;
        }

        if (result?.alreadyVerified) {
            setVerifyStep("idle");
            setVerifySeconds(0);
            setVerifyMessage(result?.message || "Email пользователя уже подтверждён.");
            return;
        }

        setVerifyStep("confirm");
        setVerifySeconds(Number(result.secondsLeft || CODE_TTL_SECONDS));
        setVerifyMessage(result?.message || "Код отправлен на текущий email пользователя.");
    };

    const confirmVerification = async () => {
        const code = String(verifyCode || "").trim();
        if (!/^\d{6}$/.test(code) || verifyBusy) return;

        setVerifyBusy(true);
        setVerifyError("");
        setVerifyMessage("");

        const result = await dispatch(adminConfirmUserEmailVerification(userDetails._id, code));

        setVerifyBusy(false);

        if (!result?.ok) {
            setVerifyError(result?.message || "Не удалось подтвердить email");
            return;
        }

        setVerifyStep("idle");
        setVerifyCode("");
        setVerifySeconds(0);
        setVerifyMessage(result?.message || "Email подтверждён.");
    };

    const requestEmailChange = async () => {
        const nextEmail = String(newEmail || "").trim().toLowerCase();

        if (!/^\S+@\S+\.\S+$/.test(nextEmail)) {
            setEmailError("Укажите корректный новый email");
            return;
        }

        if (nextEmail === String(userDetails.email || "").trim().toLowerCase()) {
            setEmailError("Новый email совпадает с текущим");
            return;
        }

        setEmailBusy(true);
        setEmailError("");
        setEmailMessage("");

        const result = await dispatch(adminRequestUserEmailChange(userDetails._id, nextEmail));

        setEmailBusy(false);

        if (!result?.ok) {
            setEmailError(result?.message || "Не удалось отправить код на новый email");
            if (result?.secondsLeft) setEmailSeconds(result.secondsLeft);
            return;
        }

        setPendingEmail(result.pendingEmail || nextEmail);
        setEmailStep("confirm");
        setEmailSeconds(Number(result.secondsLeft || CODE_TTL_SECONDS));
        setEmailMessage(result?.message || "Код отправлен на новый email.");
    };

    const confirmEmailChange = async () => {
        const code = String(emailCode || "").trim();
        if (!/^\d{6}$/.test(code) || emailBusy) return;

        setEmailBusy(true);
        setEmailError("");
        setEmailMessage("");

        const result = await dispatch(adminConfirmUserEmailChange(userDetails._id, code));

        setEmailBusy(false);

        if (!result?.ok) {
            setEmailError(result?.message || "Не удалось подтвердить новый email");
            return;
        }

        setNewEmail("");
        setPendingEmail("");
        setEmailCode("");
        setEmailStep("request");
        setEmailSeconds(0);
        setEmailMessage(result?.message || "Email пользователя изменён.");
    };

    const resetPassword = async () => {
        setPasswordBusy(true);
        setPasswordError("");
        setPasswordMessage("");

        const result = await dispatch(adminResetUserPassword(userDetails._id));

        setPasswordBusy(false);
        setPasswordConfirmOpen(false);

        if (!result?.ok) {
            setPasswordError(result?.message || "Не удалось отправить новый пароль");
            return;
        }

        setPasswordMessage(result?.message || "Новый пароль отправлен пользователю.");
    };

    const verifyMinutes = String(Math.floor(verifySeconds / 60)).padStart(2, "0");
    const verifySecondsText = String(verifySeconds % 60).padStart(2, "0");
    const emailMinutes = String(Math.floor(emailSeconds / 60)).padStart(2, "0");
    const emailSecondsText = String(emailSeconds % 60).padStart(2, "0");
    const passwordAllowed = emailVerified && userDetails.isActive !== false;

    return (
        <div className="userSection">
            <div className="userSection__head">
                <div>
                    <span>Безопасность</span>
                    <h3>Email и доступ к аккаунту</h3>
                    <p>Подтверждение текущего адреса, смена email и безопасный сброс пароля.</p>
                </div>
            </div>

            <div className="userSecurityGrid">
                <section className="userSecurityCard">
                    <div className="userSecurityCard__head">
                        <div>
                            <span>Текущий email</span>
                            <h4>{userDetails.email || "Email не указан"}</h4>
                        </div>
                        <b className={emailVerified ? "is-success" : "is-warning"}>
                            {emailVerified ? "Подтверждён" : "Не подтверждён"}
                        </b>
                    </div>

                    {!emailVerified ? (
                        <>
                            <p>Код подтверждения будет отправлен на текущий email пользователя. Подтверждение не меняет состояние «Активен / Отключён».</p>

                            {verifyStep === "confirm" ? (
                                <div className="userSecurityCard__confirm">
                                    <label>
                                        <span>Код из email</span>
                                        <input
                                            value={verifyCode}
                                            inputMode="numeric"
                                            maxLength={6}
                                            placeholder="123456"
                                            disabled={verifyBusy}
                                            onChange={(event) => {
                                                setVerifyCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                                                setVerifyError("");
                                            }}
                                        />
                                    </label>
                                    <div className="userSecurityCard__timer">
                                        <span>Код действует</span>
                                        <b>{verifyMinutes}:{verifySecondsText}</b>
                                    </div>
                                </div>
                            ) : null}

                            {verifyMessage ? <div className="userManageForm__message">{verifyMessage}</div> : null}
                            {verifyError ? <div className="userManageForm__error">{verifyError}</div> : null}

                            <div className="userSecurityCard__actions">
                                <button
                                    type="button"
                                    className="is-secondary"
                                    onClick={requestVerificationCode}
                                    disabled={verifyBusy || (verifyStep === "confirm" && verifySeconds > CODE_TTL_SECONDS - 60)}
                                >
                                    {verifyBusy ? "Отправка..." : verifyStep === "confirm" ? "Отправить код повторно" : "Отправить код подтверждения"}
                                </button>
                                {verifyStep === "confirm" ? (
                                    <button
                                        type="button"
                                        onClick={confirmVerification}
                                        disabled={verifyBusy || !/^\d{6}$/.test(String(verifyCode || "").trim())}
                                    >
                                        Подтвердить email
                                    </button>
                                ) : null}
                            </div>
                        </>
                    ) : (
                        <p>Текущий адрес подтверждён. Для замены адреса используй блок ниже.</p>
                    )}
                </section>

                <section className="userSecurityCard">
                    <div className="userSecurityCard__head">
                        <div>
                            <span>Смена email</span>
                            <h4>Новый адрес с подтверждением</h4>
                        </div>
                    </div>

                    <p>Сначала код приходит на новый адрес. Email аккаунта изменится только после ввода правильного кода.</p>

                    <label className="userSecurityCard__field">
                        <span>Новый email</span>
                        <input
                            type="email"
                            value={newEmail}
                            disabled={emailBusy || emailStep === "confirm"}
                            placeholder="new-mail@example.com"
                            onChange={(event) => {
                                setNewEmail(event.target.value);
                                setEmailError("");
                                setEmailMessage("");
                            }}
                        />
                    </label>

                    {emailStep === "confirm" ? (
                        <div className="userSecurityCard__confirm">
                            <label>
                                <span>Код с {pendingEmail || newEmail}</span>
                                <input
                                    value={emailCode}
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="123456"
                                    disabled={emailBusy}
                                    onChange={(event) => {
                                        setEmailCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                                        setEmailError("");
                                    }}
                                />
                            </label>
                            <div className="userSecurityCard__timer">
                                <span>Код действует</span>
                                <b>{emailMinutes}:{emailSecondsText}</b>
                            </div>
                        </div>
                    ) : null}

                    {emailMessage ? <div className="userManageForm__message">{emailMessage}</div> : null}
                    {emailError ? <div className="userManageForm__error">{emailError}</div> : null}

                    <div className="userSecurityCard__actions">
                        {emailStep === "confirm" ? (
                            <>
                                <button
                                    type="button"
                                    className="is-secondary"
                                    onClick={requestEmailChange}
                                    disabled={emailBusy || emailSeconds > CODE_TTL_SECONDS - 60}
                                >
                                    Отправить код повторно
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmEmailChange}
                                    disabled={emailBusy || !/^\d{6}$/.test(String(emailCode || "").trim())}
                                >
                                    Подтвердить новый email
                                </button>
                            </>
                        ) : (
                            <button type="button" onClick={requestEmailChange} disabled={emailBusy || !String(newEmail || "").trim()}>
                                {emailBusy ? "Отправка..." : "Отправить код на новый email"}
                            </button>
                        )}
                    </div>
                </section>

                <section className="userSecurityCard is-wide">
                    <div className="userSecurityCard__head">
                        <div>
                            <span>Пароль</span>
                            <h4>Сбросить и отправить новый пароль</h4>
                        </div>
                    </div>

                    <p>
                        superAdmin не видит пароль. BACKEND генерирует новый пароль, сохраняет только его bcrypt-хеш и отправляет сам пароль непосредственно пользователю.
                    </p>

                    {!passwordAllowed ? (
                        <div className="userSecurityCard__warning">
                            Сброс доступен только для активного аккаунта с подтверждённым email.
                        </div>
                    ) : null}

                    {passwordMessage ? <div className="userManageForm__message">{passwordMessage}</div> : null}
                    {passwordError ? <div className="userManageForm__error">{passwordError}</div> : null}

                    <div className="userSecurityCard__actions">
                        <button
                            type="button"
                            className="is-danger"
                            onClick={() => setPasswordConfirmOpen(true)}
                            disabled={passwordBusy || !passwordAllowed}
                        >
                            Сгенерировать и отправить новый пароль
                        </button>
                    </div>
                </section>
            </div>

            <UserConfirmDialog
                show={passwordConfirmOpen}
                title="Сбросить пароль пользователя?"
                text={`Старый пароль перестанет работать. Новый пароль будет отправлен на ${userDetails.email || "email пользователя"}. Сам пароль в ADMIN не отображается.`}
                busy={passwordBusy}
                secondaryAction={{
                    label: "Отмена",
                    onClick: () => setPasswordConfirmOpen(false),
                }}
                primaryAction={{
                    label: passwordBusy ? "Отправка..." : "Сгенерировать и отправить",
                    onClick: resetPassword,
                }}
            />
        </div>
    );
};

export default UserSecurityBlock;
